import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import { authTokenVar, isLoggedInVar } from "../apollo";
import { LOCALSTORAGE_TOKEN } from "../contants";
import { useMe } from "../modules/users/hooks/useMe";
import { Link } from "./menus/link";
import { UserRole } from "../__generated__/globalTypes";
import { AdminMenu, ClientMenu } from "./menus";
import { LogOut, Menu, X, User, ChevronDown, Sun, Moon } from "lucide-react";
import { useTheme } from "../contexts/theme-context";

import SupervisionLogo from "../assets/logo.png";

export const DashboardLayout: React.FC<any> = () => {
  const { data, refetch } = useMe();
  const { theme, toggleTheme } = useTheme();
  const [isOpened, setIsOpened] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const logout = async () => {
    localStorage.removeItem(LOCALSTORAGE_TOKEN);
    authTokenVar();
    isLoggedInVar(false);
    await refetch();
  };

  return (
    <div className="bg-gray-50 dark:bg-surface-dark min-h-screen">
      {/* Modern Header */}
      <nav className="print:hidden sticky top-0 z-50 bg-white dark:bg-surface-dark-alt border-b border-gray-200 dark:border-zinc-800 shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          {/* Logo and Brand */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsOpened(!isOpened)}
              className="xl:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
            >
              {isOpened ? (
                <X size={24} className="text-gray-600 dark:text-zinc-300" />
              ) : (
                <Menu size={24} className="text-gray-600 dark:text-zinc-300" />
              )}
            </button>

            <Link to="/">
              <div className="flex items-center gap-2 group cursor-pointer">
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-1.5 rounded-lg shadow-md group-hover:shadow-lg transition-shadow">
                  <img
                    src={SupervisionLogo}
                    alt="Supervision Logo"
                    width={24}
                    height={24}
                    className="brightness-0 invert"
                  />
                </div>
                <span className="text-xl font-bold text-primary-600 dark:text-zinc-100 mr-2">
                  Scalance
                </span>
              </div>
            </Link>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
              title={
                theme === "dark"
                  ? "Activer le thème clair"
                  : "Activer le thème sombre"
              }
            >
              {theme === "dark" ? (
                <Sun size={20} className="text-zinc-300" />
              ) : (
                <Moon size={20} className="text-gray-600" />
              )}
            </button>

            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-700 dark:text-zinc-200">
                      {data?.me?.email || "Utilisateur"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                      {data?.me?.role === UserRole.Admin
                        ? "Admin"
                        : data?.me?.role === UserRole.Employee
                          ? "Employé"
                          : "Client"}
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 dark:text-zinc-500 transition-transform ${
                    showUserMenu ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-dark-alt rounded-lg shadow-lg border border-gray-200 dark:border-zinc-800 py-1 z-40">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-zinc-800">
                      <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                        {data?.me?.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-zinc-400">
                        {data?.me?.email}
                      </p>
                    </div>
                    <button
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                    >
                      <LogOut size={16} />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar and Content */}
      <div
        className="flex h-[calc(100vh-4rem)]"
        onClick={() => setIsOpened(false)}
      >
        {/* Sidebar */}
        <aside
          className={`
            print:hidden
            fixed xl:static inset-y-0 left-0 z-[60]
            w-64 bg-white dark:bg-surface-dark-alt border-r border-gray-200 dark:border-zinc-800
            transform transition-transform duration-300 ease-in-out
            ${isOpened ? "translate-x-0" : "-translate-x-full xl:translate-x-0"}
            mt-16 xl:mt-0
          `}
        >
          {/* Mobile Backdrop */}
          {isOpened && (
            <div
              onClick={() => setIsOpened(false)}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm xl:hidden z-[45]"
            />
          )}

          {/* Menu Content */}
          <div className="relative h-full overflow-y-auto z-[60] bg-white dark:bg-surface-dark-alt">
            <div className="p-4 space-y-1">
              <div className="mb-6">
                <h3 className="px-3 text-xs font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider">
                  Navigation
                </h3>
              </div>
              {data?.me?.role === UserRole.Admin ? <AdminMenu /> : null}
              {data?.me?.role === UserRole.Client ||
              data?.me?.role === UserRole.Employee ? (
                <ClientMenu isEmployee={data?.me?.role === UserRole.Employee} />
              ) : null}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-surface-dark">
          <div className="min-h-full">
            <Outlet />
          </div>

          {/* Footer */}
          <footer className="print:hidden bg-white dark:bg-surface-dark-alt border-t border-gray-200 dark:border-zinc-800 py-3 px-4 mt-6">
            <div className="flex items-center justify-center gap-2">
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                Propulsé par
              </span>
              <span className="text-xs font-semibold text-primary-600 dark:text-zinc-200">
                Scalance
              </span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};
